import { useEffect, useId, useMemo, useRef, useState } from 'react';

/**
 * The app's one dropdown.
 *
 * Built from divs and p tags (no native <select>) so the open menu can be
 * styled the same everywhere: same trigger, same panel, same hover, same
 * checkmark. Keyboard support is wired by hand because divs give none for
 * free: Enter/Space/ArrowDown open, arrows move, Enter picks, Escape
 * closes.
 *
 * SEARCH IS ON BY DEFAULT (`searchable`). Every list this is used for -
 * people, categories, stakeholders, organisations - grows, and a list you
 * have to scroll is a list you stop reading. Pass `searchable={false}`
 * for a genuinely fixed set of two or three.
 *
 * When searchable, the open menu owns the keyboard: typing goes into the
 * box rather than jumping between options, because with a filter present
 * jump-to-letter is both redundant and surprising. Closed, typing still
 * cycles values the way a native select does.
 *
 * Usage:
 *   <Dropdown
 *     label="Department"
 *     required
 *     placeholder="Choose a department"
 *     value={departmentId}
 *     onChange={(v) => setDepartmentId(v)}
 *     options={departments.map((d) => ({ value: d.id, label: d.name }))}
 *     error={errors.departmentId}
 *   />
 *
 * As an ACTION picker - "add this person", where the control performs a
 * thing rather than holding a value - pass `value={undefined}` so the
 * trigger keeps reading its placeholder instead of latching onto the
 * last pick:
 *
 *   <Dropdown
 *     value={undefined}
 *     placeholder="Add from organisation"
 *     onChange={(id) => addPerson(id)}
 *     options={people.map((p) => ({
 *       value: p.id, label: p.displayName, description: p.email,
 *     }))}
 *   />
 *
 * `description` is both a second line AND searchable, which is what lets
 * somebody find a colleague by typing their email address.
 */

export interface DropdownOption {
  value: string;
  label: string;
  /** Second, smaller line under the label (e.g. a role or member count). */
  description?: string;
  disabled?: boolean;
}

export interface DropdownProps {
  options: DropdownOption[];
  /** The selected option's value; undefined = nothing chosen yet. */
  value: string | undefined;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Extra classes on the outer wrapper (width etc.). */
  className?: string;
  /**
   * A filter box at the top of the open menu. DEFAULT TRUE - see the note
   * above. Matches the label and the description, case-insensitively.
   */
  searchable?: boolean;
  /** Placeholder for that box. */
  searchPlaceholder?: string;
}

export function Dropdown({
  options,
  value,
  onChange,
  label,
  placeholder = 'Select...',
  hint,
  error,
  required,
  disabled,
  className,
  searchable = true,
  searchPlaceholder = 'Search…',
}: DropdownProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  /** The option the keyboard is "on" while the menu is open. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  /*
   * EVERYTHING BELOW INDEXES INTO THIS, not into `options`.
   *
   * Arrow keys, Enter and the scroll-into-view all work on the filtered
   * list; using the unfiltered one would move the highlight to rows that
   * are not on screen and pick the wrong value on Enter.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(needle) ||
        (o.description?.toLowerCase().includes(needle) ?? false),
    );
  }, [options, query]);

  // Click-away closes.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) closeMenu();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  function openMenu() {
    if (disabled) return;
    setOpen(true);
    /* A stale query would open the menu already filtered to whatever was
       typed last time, which reads as "most of my options have gone". */
    setQuery('');
    setActiveIndex(Math.max(0, options.findIndex((o) => o.value === value)));
  }

  function closeMenu() {
    setOpen(false);
    setQuery('');
  }

  // Focus the box when the menu opens, so searching needs no second click.
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  // Typing narrows the list, so the highlight must come back to the top -
  // otherwise Enter picks a row that scrolled out of the filtered set.
  useEffect(() => {
    if (open) setActiveIndex(shown.length > 0 ? 0 : -1);
  }, [query, open, shown.length]);

  function pick(option: DropdownOption) {
    if (option.disabled) return;
    onChange(option.value);
    closeMenu();
  }

  function move(delta: number) {
    if (shown.length === 0) return;
    let next = activeIndex;
    do {
      next = (next + delta + shown.length) % shown.length;
    } while (shown[next]?.disabled && next !== activeIndex);
    setActiveIndex(next);
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (disabled) return;
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (!open) openMenu();
        else if (shown[activeIndex]) pick(shown[activeIndex]);
        break;
      case ' ':
        /* Space is a CHARACTER once the search box is open - swallowing it
           would make multi-word searches impossible. */
        if (open && searchable) break;
        event.preventDefault();
        if (!open) openMenu();
        else if (shown[activeIndex]) pick(shown[activeIndex]);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!open) openMenu();
        else move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open) move(-1);
        break;
      case 'Escape':
        closeMenu();
        break;
      case 'Tab':
        closeMenu();
        break;
      default: {
        /* Type-ahead only when there is no search box to type into. With
           one open, a keystroke belongs to the filter. */
        if (open && searchable) break;
        if (event.key.length === 1 && /\S/.test(event.key)) {
          const needle = event.key.toLowerCase();
          const at = options.findIndex(
            (o) => !o.disabled && o.label.toLowerCase().startsWith(needle),
          );
          if (at >= 0) {
            if (!open) onChange(options[at]!.value);
            else setActiveIndex(at);
          }
        }
      }
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${className ?? ''}`}>
      {label && (
        <p id={`${id}-label`} className="mb-1.5 text-sm font-medium text-slate-700">
          {label}
          {!required && <span className="ml-1 font-normal text-slate-400">(optional)</span>}
        </p>
      )}

      {/* Trigger - a div acting as a combobox. */}
      <div
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleKeyDown}
        className={[
          'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm outline-none transition',
          disabled
            ? 'cursor-not-allowed bg-slate-50 text-slate-400'
            : 'bg-white text-slate-900',
          error
            ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100'
            : 'border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
        ].join(' ')}
      >
        <p className={`truncate ${selected ? '' : 'text-slate-400'}`}>
          {selected?.label ?? placeholder}
        </p>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {/* Menu - divs and p tags only. */}
      {open && (
        <div
          ref={listRef}
          role="listbox"
          aria-labelledby={label ? `${id}-label` : undefined}
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {/* Sticky: scrolling a long list must not scroll the box you are
              typing in out of sight. */}
          {searchable && (
            <div className="sticky top-0 z-10 border-b border-slate-100 bg-white px-2 pb-1.5 pt-1">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                aria-label={label ? `Search ${label}` : 'Search options'}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-400 focus:bg-white"
              />
            </div>
          )}

          {shown.length === 0 && (
            <p className="px-3 py-2 text-sm text-slate-400">
              {query.trim() ? `Nothing matches “${query.trim()}”` : 'No options'}
            </p>
          )}
          {shown.map((option, index) => {
            const isSelected = option.value === value;
            const isActive = index === activeIndex;
            return (
              <div
                key={option.value}
                data-index={index}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled || undefined}
                onClick={() => pick(option)}
                onPointerMove={() => setActiveIndex(index)}
                className={[
                  'flex cursor-pointer items-center justify-between gap-2 px-3 py-2',
                  option.disabled ? 'cursor-not-allowed opacity-40' : '',
                  isActive ? 'bg-slate-50' : '',
                ].join(' ')}
              >
                <div className="min-w-0">
                  <p className={`truncate text-sm ${isSelected ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                    {option.label}
                  </p>
                  {option.description && (
                    <p className="truncate text-xs text-slate-500">{option.description}</p>
                  )}
                </div>
                {isSelected && (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 20 20"
                    className="h-4 w-4 shrink-0 text-brand-600"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}
